/**
 * sifibank — Security Utilities
 * Input sanitization, rate limiting, and password strength helpers.
 */

// ─── Input Sanitization ───────────────────────────────────────────────────────

/**
 * Strip characters that could be used for XSS or injection attacks.
 * Safe for display in React (React already escapes, but this prevents
 * storing/transmitting malicious payloads).
 */
export function sanitizeInput(value: string): string {
  return value
    .replace(/[<>'"`;]/g, '')   // strip HTML/script injection chars
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '') // strip inline event handlers
    .trim();
}

/** Sanitize an email address — allow only valid email characters. */
export function sanitizeEmail(value: string): string {
  return value.replace(/[^a-zA-Z0-9@._+\-]/g, '').trim().toLowerCase();
}

/** Sanitize a phone number — digits, spaces, +, -, () only. */
export function sanitizePhone(value: string): string {
  return value.replace(/[^0-9\s+\-()]/g, '').trim();
}

// ─── Password Strength ────────────────────────────────────────────────────────

export type PasswordStrength = 'weak' | 'fair' | 'good' | 'strong';

export interface PasswordStrengthResult {
  score: number;          // 0–4
  label: PasswordStrength;
  color: string;          // Tailwind text color class
  barColor: string;       // Tailwind bg color class
  width: string;          // Tailwind width class for progress bar
  suggestions: string[];
}

export function evaluatePasswordStrength(password: string): PasswordStrengthResult {
  let score = 0;
  const suggestions: string[] = [];

  if (password.length >= 8) score++;
  else suggestions.push('Use at least 8 characters');

  if (password.length >= 12) score++;
  else if (password.length >= 8) suggestions.push('12+ characters makes it stronger');

  if (/[A-Z]/.test(password)) score++;
  else suggestions.push('Add uppercase letters');

  if (/[0-9]/.test(password)) score++;
  else suggestions.push('Add numbers');

  if (/[^A-Za-z0-9]/.test(password)) score++;
  else suggestions.push('Add special characters (!@#$%)');

  // Cap at 4
  const capped = Math.min(score, 4) as 0 | 1 | 2 | 3 | 4;

  const map: Record<number, Omit<PasswordStrengthResult, 'score' | 'suggestions'>> = {
    0: { label: 'weak',   color: 'text-red-400',    barColor: 'bg-red-400',    width: 'w-1/4' },
    1: { label: 'weak',   color: 'text-red-400',    barColor: 'bg-red-400',    width: 'w-1/4' },
    2: { label: 'fair',   color: 'text-amber-400',  barColor: 'bg-amber-400',  width: 'w-2/4' },
    3: { label: 'good',   color: 'text-blue-400',   barColor: 'bg-blue-400',   width: 'w-3/4' },
    4: { label: 'strong', color: 'text-green-400',  barColor: 'bg-green-400',  width: 'w-full' },
  };

  return { score: capped, suggestions, ...map[capped] };
}

// ─── Rate Limiter ─────────────────────────────────────────────────────────────

interface RateLimitEntry {
  count: number;
  firstAttempt: number;
  lockedUntil: number | null;
}

const store = new Map<string, RateLimitEntry>();

/**
 * Client-side rate limiter.
 * @param key      Unique key (e.g. 'otp-verify', 'login')
 * @param maxAttempts  Max allowed attempts within the window
 * @param windowMs     Rolling window in milliseconds (default 5 min)
 * @param lockoutMs    Lockout duration after exceeding limit (default 15 min)
 */
export function checkRateLimit(
  key: string,
  maxAttempts = 5,
  windowMs = 5 * 60 * 1000,
  lockoutMs = 15 * 60 * 1000
): { allowed: boolean; remaining: number; lockedUntilMs: number | null } {
  const now = Date.now();
  const entry = store.get(key);

  if (!entry) {
    store.set(key, { count: 1, firstAttempt: now, lockedUntil: null });
    return { allowed: true, remaining: maxAttempts - 1, lockedUntilMs: null };
  }

  // Currently locked out?
  if (entry.lockedUntil && now < entry.lockedUntil) {
    return { allowed: false, remaining: 0, lockedUntilMs: entry.lockedUntil };
  }

  // Window expired — reset
  if (now - entry.firstAttempt > windowMs) {
    store.set(key, { count: 1, firstAttempt: now, lockedUntil: null });
    return { allowed: true, remaining: maxAttempts - 1, lockedUntilMs: null };
  }

  entry.count++;

  if (entry.count > maxAttempts) {
    entry.lockedUntil = now + lockoutMs;
    store.set(key, entry);
    return { allowed: false, remaining: 0, lockedUntilMs: entry.lockedUntil };
  }

  store.set(key, entry);
  return { allowed: true, remaining: maxAttempts - entry.count, lockedUntilMs: null };
}

/** Reset the rate limit counter for a key (e.g. on successful auth). */
export function resetRateLimit(key: string): void {
  store.delete(key);
}

// ─── Secure State Helpers ─────────────────────────────────────────────────────

/**
 * Overwrite a sensitive string in state after a delay.
 * Call this after a password/PIN has been submitted.
 */
export function scheduleSecureClear(
  setters: Array<(v: string) => void>,
  delayMs = 500
): void {
  setTimeout(() => {
    setters.forEach((fn) => fn(''));
  }, delayMs);
}

// ─── OTP Expiry ───────────────────────────────────────────────────────────────

export const OTP_EXPIRY_SECONDS = 300; // 5 minutes
